import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useMountedState } from 'react-use';
import { SkullIcon } from '@wavetrak/theme';

import { SWELL_SPECTRA_GRAPH_HEIGHT } from 'common/constants';
import type { ScaleLinear } from 'd3-scale';
import { Swell } from 'types/swell';
import { BuoySpectraFormattedData } from 'types/buoys';
import styles from './ForecastGraphSwellSpectraContainer.module.scss';
import useSetupSwellSpectraGraph from '../hooks/useSetupSwellSpectraGraph';

const cx = classNames.bind(styles);
const graphHeightStyle = { height: `${SWELL_SPECTRA_GRAPH_HEIGHT}px` };

interface SwellSpectraGraphContainerProps {
  energy: Array<number> | null;
  maxSwellEnergy: number;
  periodRange: Array<number> | null | undefined;
  graphXAxisTicks: Array<number> | null;
  isBuoySwellSpectraGraph?: boolean;
  buoySpectraTooltipYPosition: BuoySpectraFormattedData | null;
  sortedTop3Swells: Array<Swell>;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
}

const ForecastGraphSwellSpectraContainer: React.FC<SwellSpectraGraphContainerProps> = ({
  energy,
  maxSwellEnergy,
  periodRange,
  graphXAxisTicks,
  isBuoySwellSpectraGraph,
  buoySpectraTooltipYPosition,
  sortedTop3Swells,
  xScale,
  yScale,
}) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const isMounted = useMountedState();

  const { setupSwellSpectraGraph } = useSetupSwellSpectraGraph({
    graphXAxisTicks,
    isBuoySwellSpectraGraph,
    buoySpectraTooltipYPosition,
    canvasRef,
    energy,
    maxSwellEnergy,
    numberOfTicks: 2,
    periodRange,
    sortedTop3Swells,
    xScale,
    yScale,
  });

  useEffect(() => {
    if (canvasRef?.current && isMounted()) setupSwellSpectraGraph();
  }, [canvasRef?.current?.offsetWidth, isMounted, setupSwellSpectraGraph]);

  return (
    <div className={cx(['swell-spectra-graph', styles.wrapper])} style={graphHeightStyle}>
      {(!periodRange || !energy) && (
        <Box className={styles.errorWrapper}>
          <Box className={styles.errorContent}>
            <SkullIcon className={styles.icon} height={20} width={20} />
            <Typography variant="callout1">Spectra data is not available for this time.</Typography>
          </Box>
        </Box>
      )}
      <div
        ref={canvasRef}
        className={cx(['swell-spectra-graph-canvas', styles.canvas])}
        data-testid="swell-spectra-graph-canvas"
      />
    </div>
  );
};

export default ForecastGraphSwellSpectraContainer;
